import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

import SelectEmails from "./SelectEmails";
import SelectWorkspace from "./SelectWorkspace";
import Submit from "./Submit";

import { WorkspaceInviteFormValues, workspaceInviteSchema } from "@/schemas/workspace-invite";
import { Form } from "@/components/ui/form";

interface CreateInviteFormProps {
  onSuccess: () => void;
  organizationId?: number; // When provided, workspace list is scoped to this organization (otherwise uses active org)
  workspace?: { name: string; id: number } | null; // When provided, workspace selector will be disabled with this workspace pre-selected (satisfies workspace requirement)
  email?: string | null; // When provided, email selector will be disabled with this email pre-filled
  excludeWorkspaceIds?: number[]; // Workspaces to exclude from the selector (e.g. workspaces the user is already in)
}

const CreateInviteForm = ({
  onSuccess,
  organizationId,
  workspace,
  email,
  excludeWorkspaceIds,
}: CreateInviteFormProps) => {
  const form = useForm<WorkspaceInviteFormValues>({
    resolver: zodResolver(workspaceInviteSchema),
    defaultValues: {
      workspace_ids: workspace?.id ? [workspace.id] : [],
      emails: email ? [email] : [],
    },
    mode: "onChange",
    reValidateMode: "onSubmit",
  });

  const formWorkspaceIds = form.watch("workspace_ids");

  return (
    <Form {...form}>
      <SelectWorkspace
        organizationId={organizationId}
        workspace={workspace}
        excludeWorkspaceIds={excludeWorkspaceIds}
      />
      <SelectEmails
        disabled={!formWorkspaceIds?.length}
        email={email}
        organizationId={organizationId}
      />
      <Submit onSuccess={onSuccess} />
    </Form>
  );
};

// Example usage:
// <CreateInviteForm onSuccess={() => undefined} /> // Normal usage with workspace selector enabled (workspace is required)
// <CreateInviteForm onSuccess={() => undefined} workspace={{name: "My Workspace", id: 123}} /> // Workspace selector disabled with "My Workspace" pre-selected (workspace requirement pre-filled)

export default CreateInviteForm;
